import React from "react";
import Amount from "./Amount";

const PricingBreakdown = ({
    addressDetail,
    TaxRatesVal,
    trackingData = [],
    showPartsSubtotal = true,
}) => {
    // Normalize trackingData to always be an array
    // Backend returns a single object for freight orders, but we need an array
    const normalizedTrackingData = React.useMemo(() => {
        if (!trackingData) return [];
        if (Array.isArray(trackingData)) return trackingData;
        // If it's a single object (freight order), wrap it in an array
        return [trackingData];
    }, [trackingData]);

    // Detect if addressDetail is a request_quote (RFQ) vs order_details
    const isRequestQuote = () => {
        return !!addressDetail?.search_quote;
    };

    // Calculate parts subtotal based on data type
    const calculatePartsSubtotal = () => {
        if (isRequestQuote()) {
            // Request quote: total_amount is parts only (doesn't include shipping)
            return parseFloat(addressDetail?.total_amount || 0) + parseFloat(addressDetail?.total_bend_price || 0);
        } else {
            // Order: total_amount includes shipping, so subtract it to get parts subtotal
            return parseFloat(addressDetail?.total_amount || 0) -
                parseFloat(addressDetail?.shipping_price || 0) -
                parseFloat(addressDetail?.tax_amount || 0);
        }
    };

    // Calculate order total based on data type
    const calculateOrderTotal = () => {
        if (isRequestQuote()) {
            // Request quote: total_amount is parts only, need to add shipping and tax
            const partsAmount = parseFloat(addressDetail?.total_amount || 0);
            const bendPrice = parseFloat(addressDetail?.total_bend_price || 0);
            const shippingPrice = parseFloat(addressDetail?.shipping_price || 0);
            const taxAmount = parseFloat(TaxRatesVal?.tax_amount || 0);
            return partsAmount + bendPrice + shippingPrice + taxAmount;
        } else {
            // Order: total_amount already includes parts + shipping + tax
            return parseFloat(addressDetail?.total_amount || 0);
        }
    };

    // Check if shipping is freight or local pickup
    const isFreightOrLocalPickup = () => {
        // Check addressDetail service_code
        const serviceCode = addressDetail?.service_code;
        if (serviceCode === 'custom_rates' || serviceCode === 'local_pickup') {
            return true;
        }
        
        // Check trackingData for freight or local pickup
        if (normalizedTrackingData && normalizedTrackingData.length > 0) {
            return normalizedTrackingData.some(tracking => 
                tracking?.service_code === 'custom_rates' || 
                tracking?.service_code === 'local_pickup' ||
                Boolean(tracking?.freight_carrier_name)
            );
        }
        
        return false;
    };

    const calculateTotalLabelCost = () => {
        // Skip calculation for freight shipping and local pickup
        if (isFreightOrLocalPickup()) return 0;
        
        if (!normalizedTrackingData || normalizedTrackingData.length === 0) return 0;

        return normalizedTrackingData.reduce((total, tracking) => {
            const labelCost = parseFloat(tracking.label_cost || 0);
            return total + labelCost;
        }, 0);
    };

    const totalLabelCost = calculateTotalLabelCost();
    const userShippingPrice = parseFloat(addressDetail?.shipping_price || 0);
    const shippingProfitLoss = userShippingPrice - totalLabelCost;
    const partsSubtotalValue = calculatePartsSubtotal();
    const orderTotalValue = calculateOrderTotal();

    return (
        <>
            {/* Divider before pricing section */}
            <hr style={{ margin: '6px 0', borderTop: '2px solid rgba(0,0,0,0.2)', width: '65%' }} />

            {/* Parts Subtotal: */}
            {showPartsSubtotal && (
                <>
                    <p className="mb-0">
                        <b className="minWidth_110">Parts Subtotal:</b>
                        <Amount amount={partsSubtotalValue} />
                    </p>
                    <p className="mb-0" style={{ fontSize: "0.85rem", color: "#6c757d", fontStyle: "italic" }}>
                        Includes service and finishing costs
                    </p>
                </>
            )}

            {/* Service cost */}
            {addressDetail?.total_bend_price !== undefined && Number(addressDetail.total_bend_price) > 0 && (
                <p className="mb-0">
                    <b className="minWidth_110">Services:</b>
                    <Amount amount={Number(addressDetail?.total_bend_price)} />
                    <br />
                </p>
            )}

            {/* User Shipping: */}
            {addressDetail?.shipping_price !== undefined && addressDetail.shipping_price > 0 && (
                <p className="mb-0">
                    <b className="minWidth_110">Shipping:</b>
                    <Amount amount={Number(addressDetail.shipping_price)} />
                </p>
            )}

            {/* Tax */}
            {TaxRatesVal?.tax_amount !== undefined && Number(TaxRatesVal.tax_amount) > 0 && (
                <p className="mb-0">
                    <b className="minWidth_110">
                        Tax <span>({TaxRatesVal.tax_percentage}%)</span>:
                    </b>
                    <Amount amount={Number(TaxRatesVal.tax_amount)} />
                </p>
            )}

            {/* Order Total: */}
            <p className="mb-0">
                <b className="minWidth_110">Order Total:</b>
                <Amount amount={orderTotalValue} />
            </p>

            {/* Divider between customer prices and company costs */}
            {totalLabelCost > 0 && (
                <hr style={{ margin: '6px 0', borderTop: '2px solid rgba(0,0,0,0.2)', width: '65%' }} />
            )}

            {totalLabelCost > 0 && (
                <p className="mb-0">
                    <b className="minWidth_110">Label Cost:</b>
                    <Amount amount={totalLabelCost} />
                </p>
            )}

            {totalLabelCost > 0 && userShippingPrice > 0 && (
                <p className="mb-0">
                    <b className="minWidth_110">Shipping {shippingProfitLoss >= 0 ? 'Profit' : 'Loss'}:</b>
                    <span style={{ color: shippingProfitLoss >= 0 ? '#01943C' : '#E11F26' }}>
                        <Amount amount={Math.abs(shippingProfitLoss)} />
                    </span>
                </p>
            )}
        </>
    );
};

export default PricingBreakdown;

